import { createContext, useContext, useMemo } from "react";
import type { RunTrace, SubCallTrace } from "../../lib/traceApi";

// TraceCompareContext threads an optional reference RunTrace through the
// tree so every SubCallRow / BridgeStepRow can detect divergence from the
// pinned baseline and surface a △ badge + side-by-side diff. Item 6 of
// the trace-view refactor (plan §7.8.5 run-vs-run compare).
//
// The lookup map is keyed by `${sub_prompt_id}|${item_key}` — the same
// identity SubCallTrace anchors use — so finding the reference for a
// given current sub-call is O(1).
export interface TraceCompareState {
  referenceRunId: string;
  referenceTrace: RunTrace | null;
  referenceSubCallByKey: Map<string, SubCallTrace>;
}

const empty: TraceCompareState = {
  referenceRunId: "",
  referenceTrace: null,
  referenceSubCallByKey: new Map(),
};

export const TraceCompareContext = createContext<TraceCompareState>(empty);

export function useTraceCompare(): TraceCompareState {
  return useContext(TraceCompareContext);
}

export function buildCompareState(
  referenceRunId: string,
  referenceTrace: RunTrace | null,
): TraceCompareState {
  const map = new Map<string, SubCallTrace>();
  if (referenceTrace) {
    for (const stage of referenceTrace.stages ?? []) {
      for (const layer of stage.layers ?? []) {
        for (const sc of layer.sub_calls ?? []) {
          map.set(subCallKey(sc.sub_prompt_id, sc.item_key), sc);
        }
      }
    }
  }
  return { referenceRunId, referenceTrace, referenceSubCallByKey: map };
}

export function subCallKey(subPromptID: string, itemKey: string): string {
  return `${subPromptID}|${itemKey}`;
}

// useMatchingReference returns the reference SubCallTrace (if any) that
// matches the given current sub-call by (sub_prompt_id, item_key) — and
// a boolean indicating whether the parsed_output of attempt[final]
// differs between current and reference.
export function useMatchingReference(current: SubCallTrace): {
  reference: SubCallTrace | null;
  diverges: boolean;
} {
  const { referenceSubCallByKey } = useTraceCompare();
  return useMemo(() => {
    const ref = referenceSubCallByKey.get(
      subCallKey(current.sub_prompt_id, current.item_key),
    );
    if (!ref) return { reference: null, diverges: false };
    const currFinal = (current.attempts ?? []).slice(-1)[0];
    const refFinal = (ref.attempts ?? []).slice(-1)[0];
    const currOut = currFinal?.parsed_output ?? null;
    const refOut = refFinal?.parsed_output ?? null;
    const diverges = !deepEqual(currOut, refOut);
    return { reference: ref, diverges };
  }, [current, referenceSubCallByKey]);
}

// deepEqual: structural equality good enough for typed parsed-output
// JSON. Tolerates key-order differences in objects (Go's encoding/json
// emits sorted keys, so this is usually moot, but safer to canonicalize).
export function deepEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (typeof a !== typeof b) return false;
  if (a === null || b === null) return a === b;
  if (typeof a !== "object") return a === b;
  if (Array.isArray(a) !== Array.isArray(b)) return false;
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
      if (!deepEqual(a[i], b[i])) return false;
    }
    return true;
  }
  const oa = a as Record<string, unknown>;
  const ob = b as Record<string, unknown>;
  const ka = Object.keys(oa);
  const kb = Object.keys(ob);
  if (ka.length !== kb.length) return false;
  for (const k of ka) {
    if (!Object.prototype.hasOwnProperty.call(ob, k)) return false;
    if (!deepEqual(oa[k], ob[k])) return false;
  }
  return true;
}
